'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { roboto } from '@/lib/fonts'
import '../../../sass/menu/OrderingWidget.scss'
import { useCart } from '@/contexts/CartContext'
import { Minus, Plus } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { Variant } from '@/lib/interfaces/ordering'
import { Currency, Language } from '@/lib/interfaces/interfaces'
import { formatMoney } from '@/lib/utils/format'

interface Props {
  isVisible: boolean;
  itemId: string;
  itemName: string;
  halfprice?: string | number;
  price?: string | number;
  tapaprice?: string | number;
  priceLabel?: string;
  halfpriceLabel?: string;
  tapapriceLabel?: string;
  priceMarkup?: number;
  closePopup: () => void;
  language: Language;
  currency: Currency;
}

// Objeto de traducciones para cada idioma
const translations = {
  es: {
    variantLabel: 'Variante',
    priceLabel: 'Precio',
    decrementQuantity: 'decrementar cantidad',
    incrementQuantity: 'incrementar cantidad',
    addToCart: 'Añadir'
  },
  en: {
    variantLabel: 'Variant',
    priceLabel: 'Price',
    decrementQuantity: 'Decrease quantity',
    incrementQuantity: 'Increase quantity',
    addToCart: 'Add to Cart'
  },
  fr: {
    variantLabel: 'Variante',
    priceLabel: 'Prix',
    decrementQuantity: 'Diminuer la quantité',
    incrementQuantity: 'Augmenter la quantité',
    addToCart: 'Ajouter'
  },
  ca: {
    variantLabel: 'Variant',
    priceLabel: 'Preu',
    decrementQuantity: 'decrementar quantitat',
    incrementQuantity: 'incrementar quantitat',
    addToCart: 'Afegir'
  },
  de: {
    variantLabel: 'Variante',
    priceLabel: 'Preis',
    decrementQuantity: 'Menge verringern',
    incrementQuantity: 'Menge erhöhen',
    addToCart: 'Hinzufügen'
  },
  eu: {
    variantLabel: 'Aldaera',
    priceLabel: 'Prezioa',
    decrementQuantity: 'gutxitu kopurua',
    incrementQuantity: 'handitu kopurua',
    addToCart: 'Gehitu'
  }
}

// Fábrica de variantes con markup aplicado. Solo incluye `label` cuando
// hay nombre personalizado (evita `label: undefined` que Firestore rechaza).
const createVariant = (type: Variant['type'], price: number, priceMarkup: number = 0, label?: string): Variant => {
  const trimmed = label?.trim()
  const variant: Variant = { type, price: price + priceMarkup }
  if (trimmed) (variant as Variant & { label: string }).label = trimmed
  return variant
}

const OrderingWidget = ({
  isVisible,
  itemId,
  itemName,
  halfprice,
  price,
  tapaprice,
  priceLabel,
  halfpriceLabel,
  tapapriceLabel,
  priceMarkup = 0,
  closePopup,
  language,
  currency
}: Props) => {
  const { addToCart } = useCart()
  const [quantity, setQuantity] = useState<number>(1)
  const [selectedVariant, setSelectedVariant] = useState<Variant['type'] | ''>('')

  // Memorizar las variantes para evitar recrearlas en cada render
  const variants: Variant[] = useMemo(() => [
    ...(price !== undefined && parseFloat(price.toString()) > 0
      ? [createVariant('full', typeof price === 'string' ? parseFloat(price) : price, priceMarkup, priceLabel)]
      : []),
    ...(halfprice !== undefined && parseFloat(halfprice.toString()) > 0
      ? [createVariant('half', typeof halfprice === 'string' ? parseFloat(halfprice) : halfprice, priceMarkup, halfpriceLabel)]
      : []),
    ...(tapaprice !== undefined && parseFloat(tapaprice.toString()) > 0
      ? [createVariant('tapa', typeof tapaprice === 'string' ? parseFloat(tapaprice) : tapaprice, priceMarkup, tapapriceLabel)]
      : [])
  ], [price, halfprice, tapaprice, priceMarkup, priceLabel, halfpriceLabel, tapapriceLabel])

  // Efecto para asignar la variante automáticamente si solo hay una disponible
  useEffect(() => {
    if (variants.length === 1) {
      setSelectedVariant(variants[0].type)
    } else if (variants.length > 1 && !variants.find(v => v.type === selectedVariant)) {
      setSelectedVariant(variants[0].type)
    }
  }, [variants, selectedVariant])

  const handleAddToCart = () => {
    const variant = variants.find((v) => v.type === selectedVariant)
    if (!variant) {
      console.error('Variante seleccionada no válida')
      return
    }

    addToCart({
      id: itemId,
      name: itemName,
      variant,
      quantity,
      cartItemId: uuidv4()
    })

    setQuantity(1)
    if (variants.length > 1) {
      setSelectedVariant(variants[0].type)
    }
    closePopup()
  }

  const handleIncrement = () => {
    setQuantity(prev => prev + 1)
  }

  const handleDecrement = () => {
    setQuantity(prev => (prev > 1 ? prev - 1 : 1))
  }

  const texts = translations[language] || translations.es
  const selectedVariantData = variants.find(v => v.type === selectedVariant)

  return (
    <div
      style={roboto.style}
      className='ordering-widget'
      onClick={(e) => e.stopPropagation()}
    >
      {/* Variantes: lista vertical de cards, maneja nombres largos (hasta 30ch) */}
      {variants.length > 1 && (
        <>
          <p className='ordering-widget__eyebrow'>{texts.variantLabel}</p>
          <div className='ordering-widget__variants' role='radiogroup' aria-label={texts.variantLabel}>
            {variants.map((variant) => {
              const isSelected = selectedVariant === variant.type
              // Fallback cuando no hay nombre personalizado: half->1/2, tapa->1/3,
              // full->"Precio" (mismo que el caso de variante única). Antes caía
              // en texts.variantLabel ("Variante") para full, de ahí la incoherencia.
              const fallbackTag = variant.type === 'half' ? '1/2' : variant.type === 'tapa' ? '1/3' : texts.priceLabel
              const displayName = variant.label?.trim() ? variant.label.trim() : fallbackTag
              return (
                <button
                  key={variant.type}
                  type='button'
                  role='radio'
                  aria-checked={isSelected}
                  className={`ordering-widget__variant ${isSelected ? 'ordering-widget__variant--selected' : ''}`}
                  onClick={() => setSelectedVariant(variant.type)}
                >
                  <span className='ordering-widget__variant-check' aria-hidden='true' />
                  <span className='ordering-widget__variant-name'>{displayName}</span>
                  <span className='ordering-widget__variant-price'>{formatMoney(variant.price, currency)}</span>
                </button>
              )
            })}
          </div>
        </>
      )}

      {variants.length === 1 && (() => {
        const v = variants[0]
        const fallbackSingle = v.type === 'half' ? '1/2' : v.type === 'tapa' ? '1/3' : texts.priceLabel
        const displaySingle = v.label?.trim() ? v.label.trim() : fallbackSingle
        return (
          <div className='ordering-widget__variants'>
            <div
              className='ordering-widget__variant ordering-widget__variant--selected ordering-widget__variant--single'
              aria-label={displaySingle}
            >
              <span className='ordering-widget__variant-check' aria-hidden='true' />
              <span className='ordering-widget__variant-name'>{displaySingle}</span>
              <span className='ordering-widget__variant-price'>{formatMoney(v.price, currency)}</span>
            </div>
          </div>
        )
      })()}

      {/* Footer: cantidad + añadir — en una fila, colapsa a columna en <380px */}
      <div className='ordering-widget__footer'>
        <div className='ordering-widget__quantity'>
          <button
            type='button'
            aria-label={texts.decrementQuantity}
            onClick={handleDecrement}
            className='ordering-widget__quantity-btn'
          >
            <Minus size={16} />
          </button>
          <input
            type='number'
            min={1}
            value={quantity}
            onChange={(e) => {
              const val = parseInt(e.target.value)
              setQuantity(isNaN(val) || val < 1 ? 1 : val)
            }}
            className='ordering-widget__quantity-value'
            aria-label={texts.priceLabel}
          />
          <button
            type='button'
            aria-label={texts.incrementQuantity}
            onClick={handleIncrement}
            className='ordering-widget__quantity-btn'
          >
            <Plus size={16} />
          </button>
        </div>

        <button
          type='button'
          onClick={handleAddToCart}
          disabled={variants.length === 0 || !selectedVariantData}
          className='ordering-widget__add'
        >
          {texts.addToCart}
          {selectedVariantData && (
            <span className='ordering-widget__add-price'>
              · {formatMoney(selectedVariantData.price * quantity, currency)}
            </span>
          )}
        </button>
      </div>
    </div>
  )
}

export default OrderingWidget
